import {
  forkJoin as observableForkJoin,
  combineLatest as observableCombineLatest,
  of as observableOf,
  Observable,
  Subscription,
  ConnectableObservable,
} from 'rxjs';

import {publishReplay, distinctUntilChanged, startWith, mergeMap, first, map} from 'rxjs/operators';
import {ChangeDetectionStrategy, Component, OnDestroy} from '@angular/core';
import {MatDialogRef} from '@angular/material/dialog';
import {ResourceValueChange} from './detail-abstract.component';
import {ResourceService} from '../resource.service';
import {Resource} from '../resource';
import {CacheService} from '../../cache/cache.service';
import {LanguageService} from '../../language/language.service';
import {Utilities} from '../../shared/utilities';
import {DisplayAttribute} from '../displayattribute';
import {EnvironmentConfigService} from '../../core/services/environment-config.service';
import {LocalDatePipe} from '../../shared/pipes/local-date.pipe';

@Component({
  selector: 'resource-summary-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <app-dialog [showInnerCard]="false">
      <app-dialog-title>
        @if (displayName) {
          <language
            key="ResourceSummaryDialogTitle"
            defaultValue="Summary of changes for {0}"
            [args]="[displayName]"
          ></language>
        } @else {
          <language
            key="ResourceSummaryDialogTitleNull"
            defaultValue="Summary of changes"
          ></language>
        }
      </app-dialog-title>
      <div>
        @if (changes != null && changes.length !== 0) {
          <mat-card appearance="raised" class="inner-card">
            <table mat-table [dataSource]="changes">
              <ng-container matColumnDef="attribute">
                <th mat-header-cell *matHeaderCellDef>
                  <language
                    key="ResourceSummaryDialogAttributeColumn"
                    defaultValue="Attribute"
                  ></language>
                </th>
                <td mat-cell *matCellDef="let change">{{ change.AttributeName | async }}</td>
              </ng-container>

              @if (showOldValues) {
                <ng-container matColumnDef="oldValue">
                  <th mat-header-cell *matHeaderCellDef>
                    <language
                      key="ResourceSummaryDialogOldValueColumn"
                      defaultValue="Old Value"
                    ></language>
                  </th>
                  <td
                    mat-cell
                    *matCellDef="let change"
                    class="valueCell"
                    [innerHtml]="getValue(change.OldValue, change.DisplayAttribute) | async"
                  ></td>
                </ng-container>
              }

              <ng-container matColumnDef="newValue">
                <th mat-header-cell *matHeaderCellDef>
                  <language
                    key="ResourceSummaryDialogNewValueColumn"
                    defaultValue="New Value"
                  ></language>
                </th>
                <td
                  mat-cell
                  *matCellDef="let change"
                  class="valueCell"
                  [innerHtml]="getValue(change.NewValue, change.DisplayAttribute) | async"
                ></td>
              </ng-container>

              @let changesColumns =
                showOldValues ? ['attribute', 'oldValue', 'newValue'] : ['attribute', 'newValue'];
              <tr mat-header-row *matHeaderRowDef="changesColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: changesColumns"></tr>
            </table>
          </mat-card>
        }
        @if (changesMultiValue != null && changesMultiValue.length !== 0) {
          <mat-card appearance="raised" class="inner-card">
            <table mat-table [dataSource]="changesMultiValue">
              <ng-container matColumnDef="attribute">
                <th mat-header-cell *matHeaderCellDef>
                  <language
                    key="ResourceSummaryDialogAttributeColumn"
                    defaultValue="Attribute"
                  ></language>
                </th>
                <td mat-cell *matCellDef="let change">{{ change.AttributeName | async }}</td>
              </ng-container>

              <ng-container matColumnDef="removedValues">
                <th mat-header-cell *matHeaderCellDef>
                  <language
                    key="ResourceSummaryDialogRemovedValuesColumn"
                    defaultValue="Removed Values"
                  ></language>
                </th>
                <td
                  mat-cell
                  *matCellDef="let change"
                  class="valueCell"
                  [innerHtml]="getValue(change.OldValue, change.DisplayAttribute) | async"
                ></td>
              </ng-container>

              <ng-container matColumnDef="addedValues">
                <th mat-header-cell *matHeaderCellDef>
                  <language
                    key="ResourceSummaryDialogAddedValuesColumn"
                    defaultValue="Added Value"
                  ></language>
                </th>
                <td
                  mat-cell
                  *matCellDef="let change"
                  class="valueCell"
                  [innerHtml]="getValue(change.NewValue, change.DisplayAttribute) | async"
                ></td>
              </ng-container>

              @let multiValueChangesColumns = ['attribute', 'removedValues', 'addedValues'];
              <tr mat-header-row *matHeaderRowDef="multiValueChangesColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: multiValueChangesColumns"></tr>
            </table>
          </mat-card>
        }
      </div>
      <app-dialog-action-button (click)="dialogRef.close(false)">
        <language key="ResourceSummaryDialogCancel" defaultValue="Cancel"></language>
      </app-dialog-action-button>
      <app-dialog-action-button [primary]="true" (click)="dialogRef.close(true)">
        <language key="ResourceSummaryDialogSave" defaultValue="Save"></language>
      </app-dialog-action-button>
    </app-dialog>
  `,
  styleUrls: ['./detail-summary-dialog.component.scss'],
  standalone: false,
})
export class ResourceDetailSummaryDialogComponent implements OnDestroy {
  public displayName: string;
  public changes: ResourceValueChange[] = null;
  public changesMultiValue: ResourceValueChange[] = null;
  public showOldValues: boolean = true;
  private observableCache: ConnectableObservable<Resource>[] = [];
  private observableConnections: Subscription[] = [];

  constructor(
    public dialogRef: MatDialogRef<ResourceDetailSummaryDialogComponent>,
    private resourceService: ResourceService,
    private cacheService: CacheService,
    private localDatePipe: LocalDatePipe,
    private envConfig: EnvironmentConfigService,
  ) {}

  ngOnDestroy(): void {
    this.observableConnections.forEach((s: Subscription) => s.unsubscribe());
  }

  getValue(value: any, displayAttribute: Observable<DisplayAttribute> = null): Observable<string> {
    if (value == null) return observableOf(value);
    if (Array.isArray(value)) {
      const vObs: Observable<any>[] = [];
      value.forEach((v: any) => {
        vObs.push(this.getValue(v, displayAttribute));
      });
      if (vObs.length === 0) {
        return this.cacheService
          .getLanguageResource()
          .pipe(
            map(lr =>
              LanguageService.translate('ResourceDetailSummaryDialogEmptyMultiValue', 'None', lr),
            ),
          );
      }

      return this.cacheService.getLanguageResource().pipe(
        first(),
        mergeMap(lr => {
          return observableCombineLatest(vObs).pipe(
            startWith([
              LanguageService.translate('ResourceDetailSummaryDialogLoading', 'Loading…', lr),
            ]),
            distinctUntilChanged(),
            map((values: any[]) => {
              let html: string = '<ul>';
              values.forEach((v: any) => {
                html += '<li>' + v + '</li>';
              });
              html += '</ul>';
              return html;
            }),
          );
        }),
      );
    } else {
      // value is no array
      if (
        typeof value === 'object' &&
        value != null &&
        (this.envConfig.getAttributeName('DisplayName') in value ||
          this.envConfig.getAttributeName('ObjectID') in value)
      ) {
        if (value[this.envConfig.getAttributeName('DisplayName')] == null) {
          return observableForkJoin(
            this.cacheService.getLanguageResource().pipe(first()),
            this.cacheService
              .getDisplayAttribute(
                this.envConfig.getAttributeName('DisplayName'),
                this.envConfig.getObjectTypeName('Resource'),
              )
              .pipe(first()),
          ).pipe(
            map(([lr, da]) => {
              return (
                '⟨ ' + LanguageService.translate('DatatableNo', 'No', lr) + ' ' + da.Caption + ' ⟩'
              );
            }),
          );
        } else {
          return observableOf(
            Utilities.escapeHTML(value[this.envConfig.getAttributeName('DisplayName')]),
          );
        }
      } else if (ResourceService.guidRegex.test(value)) {
        let obs = this.observableCache[value];
        if (obs == null) {
          obs = this.resourceService
            .getBasicResource(value)
            .pipe(distinctUntilChanged(), publishReplay(1)) as ConnectableObservable<any>;
          this.observableConnections.push(obs.connect());
          this.observableCache[value] = obs;
        }

        return obs.pipe(
          mergeMap((r: Resource) => {
            if (r == null) return observableOf(Utilities.escapeHTML(value));
            if (r[this.envConfig.getAttributeName('DisplayName')] == null) {
              return observableForkJoin(
                this.cacheService.getLanguageResource().pipe(first()),
                this.cacheService
                  .getDisplayAttribute(
                    this.envConfig.getAttributeName('DisplayName'),
                    this.envConfig.getObjectTypeName('Resource'),
                  )
                  .pipe(first()),
                (lr: any, da: DisplayAttribute) => {
                  return (
                    '⟨ ' +
                    LanguageService.translate('DatatableNo', 'No', lr) +
                    ' ' +
                    da.Caption +
                    ' ⟩'
                  );
                },
              );
            } else {
              return observableOf(
                Utilities.escapeHTML(r[this.envConfig.getAttributeName('DisplayName')]),
              );
            }
          }),
        );
      } else {
        if (displayAttribute == null) {
          return observableOf(Utilities.escapeHTML(value));
        } else {
          return displayAttribute.pipe(
            mergeMap(da => {
              if (da.DataType === 'Binary') {
                return this.cacheService.getLanguageResource().pipe(
                  map(lr => {
                    return (
                      '<i>' +
                      Utilities.escapeHTML(
                        LanguageService.translate(
                          'RequestApprovalBinary',
                          'Binary (not shown)',
                          lr,
                        ),
                      ) +
                      '</i>'
                    );
                  }),
                );
              } else if (da.DataType === 'DateTime') {
                return this.localDatePipe.transform(value).pipe(distinctUntilChanged());
              } else {
                return observableOf(Utilities.escapeHTML(value));
              }
            }),
          );
        }
      }
    }
  }
}
